import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar } from '@mui/material';
import ReportToolbar from './ReportToolbar';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmt     = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
const fmtQty  = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PurchaseSummaryBillWise = () => {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);
  const [search, setSearch]     = useState('');

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'PurchaseReportApi',
        `/PurchaseSummaryBillDetails/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch purchase summary bill wise report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]);

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.BillNumber  || '').toLowerCase().includes(q) ||
      (r.SupplierName|| '').toLowerCase().includes(q) ||
      (r.GSTNumber   || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // Totals
  const totalQty      = filtered.reduce((s, r) => s + (parseFloat(r.TotalQty)       || 0), 0);
  const totalGross    = filtered.reduce((s, r) => s + (parseFloat(r.GrossAmount)     || 0), 0);
  const totalDiscount = filtered.reduce((s, r) => s + (parseFloat(r.TotalDiscount)   || 0), 0);
  const totalTaxable  = filtered.reduce((s, r) => s + (parseFloat(r.InvoiceTaxable)  || 0), 0);
  const totalCGST     = filtered.reduce((s, r) => s + (parseFloat(r.InvoiceCGST)     || 0), 0);
  const totalSGST     = filtered.reduce((s, r) => s + (parseFloat(r.InvoiceSGST)     || 0), 0);
  const totalIGST     = filtered.reduce((s, r) => s + (parseFloat(r.InvoiceIGST)     || 0), 0);
  const totalAmount   = filtered.reduce((s, r) => s + (parseFloat(r.InvoiceTotal)    || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'PurchaseSummaryBillWise', 0);
    if (!filtered.length) return;
    const colHeaders = ['#', 'Bill No', 'Bill Date', 'Supplier', 'GST No', 'Total Qty', 'Gross Amt', 'Discount', 'Taxable', 'CGST', 'SGST', 'IGST', 'Total'];
    const rows = filtered.map((r, i) => [
      i + 1, r.BillNumber, fmtDate(r.BillDate), r.SupplierName, r.GSTNumber || '',
      parseFloat(r.TotalQty) || 0, parseFloat(r.GrossAmount) || 0,
      parseFloat(r.TotalDiscount) || 0, parseFloat(r.InvoiceTaxable) || 0,
      parseFloat(r.InvoiceCGST) || 0, parseFloat(r.InvoiceSGST) || 0,
      parseFloat(r.InvoiceIGST) || 0, parseFloat(r.InvoiceTotal) || 0,
    ]);

      await exportStyledExcel({
          reportTitle: 'PURCHASE SUMMARY BILL WISE',
          sheetName: 'Purchase Summary',
          fileName: `PurchaseSummaryBillWise_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: colHeaders,
          rows,
          totalsRow: ['', '', '', '', 'Totals', totalQty, totalGross, totalDiscount, totalTaxable, totalCGST, totalSGST, totalIGST, totalAmount],
          columnWidths: [4,14,12,22,16,10,12,12,12,10,10,10,12],
      });
  };

  const th  = { padding: '8px 10px', textAlign: 'left',   fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
  const td  = { padding: '7px 10px', textAlign: 'left',   fontSize: 12, whiteSpace: 'nowrap' };
  const tdR = { padding: '7px 10px', textAlign: 'right',  fontSize: 12, whiteSpace: 'nowrap' };

  const PRIMARY = '#0d8392';
const PRIMARY_MID = '#1aabb9';
const PRIMARY_LT = '#e0f7fa';

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading purchase summary bill wise..." />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <ReportToolbar
          title="Purchase Summary Bill Wise"
          onBack={() => history.push('/ReportsHub')}
          search={search}
          onSearchChange={setSearch}
          fromDate={fromDate}
          onFromDate={setFromDate}
          toDate={toDate}
          onToDate={setToDate}
          onRefresh={fetchReport}
          loading={loading}
          onExport={handleExport}
          exportDisabled={!filtered.length}
          searchPlaceholder="Search bill, supplier..."
        />

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 900 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Bill No</th>
                <th style={th}>Bill Date</th>
                <th style={th}>Supplier</th>
                <th style={th}>GST No</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Gross Amt</th>
                <th style={{ ...th, textAlign: 'right' }}>Discount</th>
                <th style={{ ...th, textAlign: 'right' }}>Taxable</th>
                <th style={{ ...th, textAlign: 'right' }}>CGST</th>
                <th style={{ ...th, textAlign: 'right' }}>SGST</th>
                <th style={{ ...th, textAlign: 'right' }}>IGST</th>
                <th style={{ ...th, textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => (
                  <tr key={r.PurchaseId || i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                    <td style={td}>{i + 1}</td>
                    <td style={{ ...td, fontWeight: 700, color: PRIMARY }}>{r.BillNumber}</td>
                    <td style={td}>{fmtDate(r.BillDate)}</td>
                    <td style={{ ...td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.SupplierName}</td>
                    <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{r.GSTNumber || '-'}</td>
                    <td style={tdR}>{fmtQty(r.TotalQty)}</td>
                    <td style={tdR}>{fmt(r.GrossAmount)}</td>
                    <td style={{ ...tdR, color: '#dc2626' }}>{fmt(r.TotalDiscount)}</td>
                    <td style={tdR}>{fmt(r.InvoiceTaxable)}</td>
                    <td style={tdR}>{fmt(r.InvoiceCGST)}</td>
                    <td style={tdR}>{fmt(r.InvoiceSGST)}</td>
                    <td style={tdR}>{fmt(r.InvoiceIGST)}</td>
                    <td style={{ ...tdR, fontWeight: 700 }}>{fmt(r.InvoiceTotal)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmtQty(totalQty)}</td>
                  <td style={tdR}>{fmt(totalGross)}</td>
                  <td style={tdR}>{fmt(totalDiscount)}</td>
                  <td style={tdR}>{fmt(totalTaxable)}</td>
                  <td style={tdR}>{fmt(totalCGST)}</td>
                  <td style={tdR}>{fmt(totalSGST)}</td>
                  <td style={tdR}>{fmt(totalIGST)}</td>
                  <td style={tdR}>{fmt(totalAmount)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default PurchaseSummaryBillWise;


